package org.zq.flvtomp4.queue

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.Semaphore
import kotlinx.coroutines.sync.withLock
import kotlinx.coroutines.sync.withPermit
import org.zq.flvtomp4.converter.ConvertEvent
import org.zq.flvtomp4.converter.ConvertMode
import org.zq.flvtomp4.converter.ConvertRequest
import org.zq.flvtomp4.converter.VideoConverter
import org.zq.flvtomp4.platform.PlatformFiles

class ConvertQueueManager(
    private val converterFactory: (AppSettings) -> VideoConverter,
    private val scope: CoroutineScope,
) {
    private val _tasks = MutableStateFlow<List<ConvertTask>>(emptyList())
    val tasks: StateFlow<List<ConvertTask>> = _tasks.asStateFlow()

    private val mutex = Mutex()
    private val runningJobs = mutableMapOf<String, Job>()
    private val activeConverters = mutableMapOf<String, VideoConverter>()
    private var remuxSemaphore = Semaphore(3)
    private var transcodeSemaphore = Semaphore(1)
    private var settings = AppSettings()

    fun updateSettings(newSettings: AppSettings) {
        settings = newSettings
        remuxSemaphore = Semaphore(newSettings.maxConcurrentRemux.coerceAtLeast(1))
        transcodeSemaphore = Semaphore(newSettings.maxConcurrentTranscode.coerceAtLeast(1))
    }

    fun currentSettings(): AppSettings = settings

    suspend fun addFiles(paths: List<String>) {
        val flvFiles = PlatformFiles.collectFlvFiles(paths)
        if (flvFiles.isEmpty()) return

        mutex.withLock {
            val existing = _tasks.value.map { it.inputPath }.toSet()
            val outputDir = when (settings.outputLocation) {
                OutputLocation.SAME_AS_SOURCE -> null
                OutputLocation.CUSTOM -> settings.customOutputDir
            }
            val overwrite = settings.overwritePolicy == OverwritePolicy.OVERWRITE

            val newTasks = flvFiles
                .filterNot { it in existing }
                .map { input ->
                    ConvertTask(
                        id = generateId(),
                        inputPath = input,
                        fileName = PlatformFiles.fileName(input),
                        outputPath = PlatformFiles.resolveOutputPath(
                            inputPath = input,
                            outputDir = outputDir,
                            overwrite = overwrite,
                        ),
                    )
                }

            if (newTasks.isNotEmpty()) {
                _tasks.update { it + newTasks }
            }
        }
    }

    fun startAll() {
        val waiting = _tasks.value.filter { it.status == TaskStatus.WAITING }
        waiting.forEach { enqueue(it.id) }
    }

    fun startTask(taskId: String) {
        enqueue(taskId)
    }

    fun cancelTask(taskId: String) {
        scope.launch {
            activeConverters[taskId]?.cancel(taskId)
            runningJobs.remove(taskId)?.cancel()
            updateTask(taskId) {
                copy(status = TaskStatus.CANCELLED, progress = 0f)
            }
        }
    }

    fun clearCompleted() {
        _tasks.update { list ->
            list.filterNot {
                it.status == TaskStatus.COMPLETED ||
                    it.status == TaskStatus.FAILED ||
                    it.status == TaskStatus.CANCELLED
            }
        }
    }

    fun removeTask(taskId: String) {
        cancelTask(taskId)
        _tasks.update { list -> list.filterNot { it.id == taskId } }
    }

    fun overallProgress(): Pair<Int, Int> {
        val list = _tasks.value
        val done = list.count {
            it.status == TaskStatus.COMPLETED ||
                it.status == TaskStatus.FAILED ||
                it.status == TaskStatus.CANCELLED
        }
        return done to list.size
    }

    fun currentSpeed(): String? {
        return _tasks.value
            .firstOrNull { it.status == TaskStatus.CONVERTING && it.speed != null }
            ?.speed
    }

    fun estimatedRemainingMs(): Long? {
        val converting = _tasks.value.filter { it.status == TaskStatus.CONVERTING }
        if (converting.isEmpty()) return null

        var total = 0L
        converting.forEach { task ->
            if (task.durationMs > 0 && task.progress > 0.01f) {
                val remainingRatio = (1f - task.progress).coerceAtLeast(0f)
                total += (task.durationMs * remainingRatio).toLong()
            }
        }

        val waiting = _tasks.value.count { it.status == TaskStatus.WAITING }
        if (waiting > 0) {
            val avg = converting.map { it.durationMs }.filter { it > 0 }.average()
            if (!avg.isNaN()) {
                total += (avg * waiting).toLong()
            }
        }

        return total.takeIf { it > 0 }
    }

    private fun enqueue(taskId: String) {
        val task = _tasks.value.firstOrNull { it.id == taskId } ?: return
        if (task.status != TaskStatus.WAITING) return
        if (runningJobs.containsKey(taskId)) return

        val job = scope.launch {
            val preferRemux = settings.mode == ConvertMode.REMUX ||
                settings.mode == ConvertMode.SMART
            val semaphore = if (preferRemux) remuxSemaphore else transcodeSemaphore

            semaphore.withPermit {
                runConversion(taskId)
            }
        }
        runningJobs[taskId] = job
        job.invokeOnCompletion {
            runningJobs.remove(taskId)
            activeConverters.remove(taskId)
        }
    }

    private suspend fun runConversion(taskId: String) {
        val task = _tasks.value.firstOrNull { it.id == taskId } ?: return
        if (task.status == TaskStatus.CANCELLED) return

        updateTask(taskId) {
            copy(status = TaskStatus.ANALYZING, errorMessage = null, logs = emptyList())
        }

        val request = ConvertRequest(
            taskId = task.id,
            inputPath = task.inputPath,
            outputPath = task.outputPath,
            mode = settings.mode,
            quality = settings.quality,
            overwrite = settings.overwritePolicy == OverwritePolicy.OVERWRITE,
        )

        val converter = converterFactory(settings)
        activeConverters[taskId] = converter

        try {
            converter.convert(request).collect { event ->
                handleEvent(event)
            }
        } catch (_: kotlinx.coroutines.CancellationException) {
            updateTask(taskId) { copy(status = TaskStatus.CANCELLED) }
        } finally {
            activeConverters.remove(taskId)
        }
    }

    private suspend fun handleEvent(event: ConvertEvent) {
        when (event) {
            is ConvertEvent.Started -> updateTask(event.taskId) {
                copy(
                    status = TaskStatus.CONVERTING,
                    durationMs = event.durationMs,
                    progress = 0f,
                )
            }

            is ConvertEvent.Progress -> updateTask(event.taskId) {
                copy(
                    status = TaskStatus.CONVERTING,
                    progress = event.progress,
                    processedMs = event.processedMs,
                    speed = event.speed,
                )
            }

            is ConvertEvent.Log -> updateTask(event.taskId) {
                val nextLogs = (logs + event.message).takeLast(200)
                copy(logs = nextLogs)
            }

            is ConvertEvent.Completed -> updateTask(event.taskId) {
                copy(
                    status = TaskStatus.COMPLETED,
                    progress = 1f,
                    outputPath = event.outputPath,
                    errorMessage = null,
                )
            }

            is ConvertEvent.Failed -> updateTask(event.taskId) {
                copy(
                    status = TaskStatus.FAILED,
                    errorMessage = event.message,
                )
            }

            is ConvertEvent.Cancelled -> updateTask(event.taskId) {
                copy(status = TaskStatus.CANCELLED)
            }
        }
    }

    private fun updateTask(taskId: String, transform: ConvertTask.() -> ConvertTask) {
        _tasks.update { list ->
            list.map { if (it.id == taskId) it.transform() else it }
        }
    }

    private var idCounter = 0L
    private fun generateId(): String {
        idCounter += 1
        return "task-$idCounter-${kotlin.random.Random.nextInt(1000, 9999)}"
    }
}
